import { ReorderableFormSectionChild, TagGroup, UseModalHook } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { ConfigureBidGroupsStepQuery } from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep.generated";
import { BasePlanGroupDimensionsModal } from "src/routes/bids/plan-based-bid-package/components/BasePlanGroupDimensionsModal";
import { BidPlanDimensionRows } from "src/routes/bids/plan-based-bid-package/components/BidPlanDimensionRows";
import {
  groupKey,
  PlanBasedBidPackageBidPlanGroup,
  PlanBasedBidPackageFormState,
  renumberGroups,
} from "src/routes/bids/plan-based-bid-package/utils";

type BuildPersonalizationZoneChildSectionParams = {
  formState: ObjectState<PlanBasedBidPackageFormState>;
  group: ObjectState<PlanBasedBidPackageBidPlanGroup>;
  dimensionResults: ConfigureBidGroupsStepQuery["potentialBidPlanDimensions"];
  productOfferings: ConfigureBidGroupsStepQuery["bidPackage"]["productOfferings"];
  openModal: UseModalHook["openModal"];
};

/**
 * Builds one Personalization Zone's `childSection`. `id`/`orderField` make this natively draggable —
 * Beam's `FormSection` renders a drag handle and reorders `childSections` on drop by permuting
 * their `orderField` values directly, so no manual `DnDGrid`/`onReorder` wiring is needed here.
 */
export function buildPersonalizationZoneChildSection(
  params: BuildPersonalizationZoneChildSectionParams,
): ReorderableFormSectionChild {
  const { formState, group, dimensionResults, productOfferings, openModal } = params;
  // The zone is titled with its primary personalization option group's name
  const personalizationGroupId = group.primaryOptionGroupId.value;
  const personalizationResult = personalizationGroupId
    ? dimensionResults.find((r) => r.group?.id === personalizationGroupId)
    : undefined;
  const personalizationName = personalizationResult?.displayName;
  const planNames = personalizationResult
    ? personalizationResult.productOfferings.map((po) => po.name)
    : (formState.productOfferings.value ?? []).map((id) => productOfferings.find((po) => po.id === id)?.name).compact();

  function openEditModal() {
    openModal({
      content: (
        <BasePlanGroupDimensionsModal formState={formState} dimensionResults={dimensionResults} editingGroup={group} />
      ),
    });
  }

  function removeGroup() {
    const key = groupKey(group.value);
    const next = (formState.bidPlanGroups.value ?? []).filter((g) => groupKey(g) !== key);
    formState.bidPlanGroups.set(renumberGroups(next));
  }

  return {
    id: groupKey(group.value)!,
    orderField: group.order,
    title: personalizationName ?? "Personalization Zone",
    actions: [
      { kind: "icon", icon: "pencil", onClick: openEditModal, label: "Edit Personalization Zone" },
      { kind: "icon", icon: "trash", onClick: removeGroup, label: "Remove Personalization Zone" },
    ],
    fields: (
      <>
        {planNames.nonEmpty && <TagGroup tags={planNames.map((name) => ({ text: name }))} />}
        <BidPlanDimensionRows group={group} dimensionResults={dimensionResults} title={personalizationName} />
      </>
    ),
  };
}
